import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Select,
  SelectItem,
  Input,
  Spinner,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Slider,
  Pagination,
} from "@nextui-org/react";
import axios from "axios";
import { FiDownload } from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";

export default function CommissionReport() {
  /* ───────── State ───────── */
  const [account, setAccount] = useState("All");
  const [supplierSel, setSupplier] = useState("All");
  const [searchTerm, setSearch] = useState("");
  const today = new Date();
  const priorDate = new Date();
  priorDate.setDate(today.getDate() - 30);

  const formatInputDate = (d) => d.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(formatInputDate(priorDate));
  const [endDate, setEndDate] = useState(formatInputDate(today));

  const [rateRange, setRateRange] = useState([0, 10]);

  const [supplierOpts, setSupplierOpts] = useState([]); // [{ id, name }]
  const [optsLoading, setOptsLoading] = useState(false);

  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;
  const navigate = useNavigate();
  const [reportType, setReportType] = useState("Report");
  const [loading, setLoading] = useState(true);
    const location = useLocation();

  /* ───────── API fetch ───────── */
  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/admin/reports/commisstion-rate/supplier`,
        {
          params: {
            pageSize: 1000,
            startDate: startDate ? `${startDate}T00:00:00.000Z` : undefined,
            endDate: endDate ? `${endDate}T23:59:59.999Z` : undefined,
            supplierId: supplierSel === "All" ? undefined : supplierSel,
            account: account === "All" ? undefined : account,
            search: searchTerm.trim() || undefined,
            minRate: rateRange[0],
            maxRate: rateRange[1],
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      const apiData = res.data?.details ?? {};
      setRows(apiData.data || []);
    } catch (err) {
      console.error("API fetch error:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  /* fire when any filter changes */
  useEffect(() => {
    fetchData();
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, account, supplierSel, searchTerm, rateRange]);

  /* ───────── Supplier live‑search (fills datalist) ───────── */
  useEffect(() => {
    if (!searchTerm) {
      setSupplierOpts([]);
      return;
    }

    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      setOptsLoading(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/admin/reports/get-supplier`,
          {
            params: { name: searchTerm },
            signal: ctrl.signal,
            headers: {
              Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            },
          }
        );
        setSupplierOpts(res.data?.data ?? []);
      } catch (e) {
        if (!axios.isCancel(e)) console.error("Supplier search error", e);
        setSupplierOpts([]);
      } finally {
        setOptsLoading(false);
      }
    }, 400);

    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [searchTerm]);

  /* ───────── Client‑side filters (extra safety) ───────── */
  const filteredRows = useMemo(() => {
    let data = rows;

    // Date range filter
    if (startDate || endDate) {
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(`${endDate}T23:59:59.999Z`) : null;
      data = data.filter((r) => {
        const d = new Date(r.createdAt);
        if (start && d < start) return false;
        if (end && d > end) return false;
        return true;
      });
    }

    // Account filter
    if (account !== "All") {
      data = data.filter(
        (r) => (r.paymentMethod ?? "").toLowerCase() === account.toLowerCase()
      );
    }

    // Rate range filter
    data = data.filter((r) => {
      const rate = Number(r.commissionRate ?? 0);
      return rate >= rateRange[0] && rate <= rateRange[1];
    });

    // Search by supplier name / id
    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      data = data.filter((r) => {
        const fullName = `${r.Quote?.User?.first_name ?? ""} ${
          r.Quote?.User?.last_name ?? ""
        }`.toLowerCase();
        const idStr = String(r.Quote?.User?.id ?? "").toLowerCase();
        return fullName.includes(q) || idStr.includes(q);
      });
    }

    return data;
  }, [rows, startDate, endDate, account, searchTerm, rateRange]);

  const totalCommissionPrice = filteredRows.reduce(
    (sum, r) => sum + (r.application_fee_amount ?? 0),
    0
  );
  /* ───────── Helpers ───────── */
  const totalCommissionAmount = filteredRows.reduce(
    (sum, r) => sum + (r.Quote?.total_price ?? 0),
    0
  );

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage]);
  const formatDate = (iso) =>
    iso
      ? new Date(iso).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "-";

  const downloadCSV = useCallback(() => {
    const header = [
      "Supplier",
      "Supplier Amount",
      "Date",
      "Price",
      "Rate",
      "Account",
      "WreckConnect Amount",
    ];

    const rowsCsv = filteredRows.map((r) => {
      const supplier =
        `${r?.Quote?.User?.first_name ?? ""} ${
          r?.Quote?.User?.last_name ?? ""
        }`.trim() || "Anonymous";
      const supplierAmt =
        (r?.Quote?.total_price ?? 0) - (r?.application_fee_amount ?? 0);
      const date = formatDate(r?.createdAt);
      const price = r?.Quote?.total_price ?? 0;
      const rate = r?.commissionRate ?? "N/A";
      const account = r?.paymentMethod ?? "";
      const wreck = r?.application_fee_amount ?? 0;
      return [supplier, supplierAmt, date, price, rate, account, wreck]
        .map((v) => `"${v}"`) // quote values => Excel‑safe
        .join(",");
    });

    const csv = [header.join(","), ...rowsCsv].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "commission_report.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  });
  useEffect(() => {
    if (location.pathname === "/report") {
      setReportType("Report");
    } else if (location.pathname === "/commissionReport") {
      setReportType("Commission Report");
    }
  }, [location.pathname]);
  /* ───────── UI ───────── */
  return (
    <div className="p-4 space-y-6">
      <span className="mb-4  text-sm font-semibold ">
        <Select
          size="sm"
          selectedKeys={[reportType]}
          onSelectionChange={(keys) => {
            const selected = [...keys][0];
            if (selected === "Report") {
              navigate("/report");
            } else if (selected === "Commission Report") {
              navigate("/commissionReport");
            }
          }}
          aria-label="Report type"
          className="w-48  rounded-sm"
          classNames={{
            trigger:
              "h-8 px-3 border rounded-md flex items-center justify-center",
            value: "text-sm font-medium text-center",
            selectorIcon: "absolute right-2 top-1/2 -translate-y-1/2",
            innerWrapper: "pt-0",
          }}
        >
          <SelectItem key="Report">Quote Report</SelectItem>
          <SelectItem key="Commission Report">Commission Report</SelectItem>
        </Select>
      </span>{" "}
      <div className="flex gap-5">
        <Card className="w-52 h-24 shadow-md pl-2">
          {" "}
          {/* adjust width and height as needed */}
          <CardHeader className="flex-col items-start justify-center h-full text-left">
            <h2 className="text-lg font-semibold">Total Commission</h2>
            <div className="text-xl font-bold text-green-600 break-all">
              ${totalCommissionPrice.toLocaleString()}
            </div>
          </CardHeader>
        </Card>

        <Card className="w-52 h-24 shadow-md pl-2">
          {" "}
          {/* same width and height as above */}
          <CardHeader className="flex-col items-start justify-center h-full text-left">
            <h2 className="text-lg font-semibold">Total Amount</h2>
            <span className="text-xl font-bold text-green-600 break-all">
              ${totalCommissionAmount.toLocaleString()}
            </span>
          </CardHeader>
        </Card>
      </div>
      {/* Commission Rate Slider */}
      <div className="flex flex-col w-[30%] min-w-[200px]">
        <label className="mb-1 text-sm font-medium">Commission Rate (%)</label>
        <Slider
          aria-label="Commission Rate"
          size="sm"
          step={0.5}
          minValue={0}
          maxValue={20}
          value={rateRange}
          onChange={setRateRange}
          className="max-w-full"
          showTooltip
          formatOptions={{ style: "decimal", minimumFractionDigits: 1 }}
        />
        <div className="text-sm mt-1 text-default-500">
          Range: {rateRange[0]}% – {rateRange[1]}%
        </div>
      </div>
      {/* Date Range Filter */}
      <div className="flex items-start gap-2">
        {/* Date Range Box */}
        <div className="border rounded-xl p-2 flex items-end gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-semibold text-gray-600">
              Start Date
            </label>
            <Input
              type="date"
              size="sm"
              className="w-[140px]"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <span className="pb-2 font-semibold">—</span>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-semibold text-gray-600">
              End Date
            </label>
            <Input
              type="date"
              size="sm"
              className="w-[140px]"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate}
            />
          </div>
          <Button
            size="sm"
            color="gray"
            variant="bordered"
            radius="2xl"
            className="ml-2 px-3 font-semibold"
            isDisabled={!startDate && !endDate}
            onPress={() => {
              setStartDate("");
              setEndDate("");
            }}
          >
            <span className="text-base leading-none">×</span>
            Clear
          </Button>
        </div>

        {/* Account Select */}
        <div className="flex flex-col gap-1 border rounded-xl p-2">
          <label className="text-sm font-semibold text-gray-600">Account</label>
          <Select
            size="sm"
            className="w-40"
            selectedKeys={[account]}
            onSelectionChange={(k) => setAccount([...k][0])}
          >
            <SelectItem key="All">All</SelectItem>
            <SelectItem key="Stripe">Stripe</SelectItem>
            <SelectItem key="Account">Account</SelectItem>
            <SelectItem key="Capricorn">Capricorn</SelectItem>
          </Select>
        </div>
      </div>
      {/* Account & Supplier Search */}
      <div className="flex flex-col lg:flex-row flex-wrap gap-4">
        {/* Account */}

        {/* Supplier search with datalist */}
        <div className="flex flex-col flex-1">
          <label className="text-sm font-medium mb-1">
            Search Supplier / ID
          </label>
          <input
            list="supplierList"
            className="border rounded-lg px-3 py-2 text-sm w-full outline-none focus:ring"
            placeholder="Type name or ID..."
            value={searchTerm}
            onChange={(e) => {
              setSearch(e.target.value);
              setSupplier("All"); // reset selected id, real id will set on option select below
            }}
          />
          {/* suggestions list */}
          <datalist id="supplierList">
            {supplierOpts.map((opt) => (
              <option
                key={opt.id}
                value={opt.name}
                onClick={() => {
                  setSupplier(opt.id);
                  setSearch(opt.name);
                }}
              />
            ))}
          </datalist>
          {optsLoading && <span className="text-xs mt-1">Loading...</span>}
        </div>
        <div className="mt-4">
          <Button
            size="sm"
            color="primary"
            startContent={<FiDownload />}
            className="mt-2 h-9 rounded-md"
            onPress={downloadCSV}
          >
            Download CSV
          </Button>
        </div>
      </div>
      {/* Total Commission */}
      {/* Data Table */}
      <Card>
        <CardBody>
          {loading ? (
            <div className="flex justify-center py-20">
              <Spinner size="lg" label="Fetching data…" />
            </div>
          ) : (
            <div className="overflow-x-auto overflow-y-auto max-h-[600px]">
              <Table removeWrapper className="w-full table-auto">
                <TableHeader>
                  <TableColumn className="text-center">Supplier</TableColumn>
                  <TableColumn className="text-center">
                    Supplier Amount
                  </TableColumn>
                  <TableColumn className="text-center">Date</TableColumn>
                  <TableColumn className="text-center">Price</TableColumn>
                  <TableColumn className="text-center">Rate</TableColumn>
                  <TableColumn className="text-center">Account</TableColumn>
                  <TableColumn className="text-center">
                    WreckConnect Amount
                  </TableColumn>
                </TableHeader>

                <TableBody emptyContent="No results">
                  {paginatedRows.map((r, i) => (
                    <TableRow key={r.id ?? `${r.createdAt}-${i}`}>
                      <TableCell className="text-center py-3">
                        {[r.Quote?.User?.first_name, r.Quote?.User?.last_name]
                          .filter(Boolean)
                          .join(" ") || "Anonymous"}
                      </TableCell>
                      <TableCell className="text-center">
                        $
                        {(
                          r.Quote?.total_price - r.application_fee_amount
                        )?.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-center">
                        {formatDate(r.createdAt)}
                      </TableCell>
                      <TableCell className="text-center">
                        ${r.Quote?.total_price?.toLocaleString?.()}
                      </TableCell>
                      <TableCell className="text-center">
                        {r.commissionRate ?? "N/A"}%
                      </TableCell>
                      <TableCell className="text-center">
                        {r.paymentMethod}
                      </TableCell>
                      <TableCell className="text-center">
                        ${r.application_fee_amount?.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>
      {filteredRows.length > pageSize && (
        <div className="flex justify-center  mt-4">
          <Pagination
            showControls
            total={Math.ceil(filteredRows.length / pageSize)}
            page={currentPage}
            className="bg-[#f5f5f5] rounded-xl p-1"
            onChange={setCurrentPage}
            color="primary"
            variant="flat" // blue highlight
          />
        </div>
      )}
    </div>
  );
}
